import { Dropdown } from "@fanduel/og-fe-library";
import React from "react";
import PoweredByNumberFire from "@/components/svg/PoweredByNumberFire";
import { PLAYER_NEWS_FILTER_APPLIED } from "@/constants/amplitude";
import { findSlugByType } from "@/constants/playerNews";
import { FilterOptions, FilterState } from "@/types/playerNews";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { replaceLastSegment } from "@/utils/index";
import styles from "./playerNewsHeader.module.scss";

interface PlayerNewsHeaderProps {
  title: string;
  sportFilters: FilterOptions;
  onFilterChange: (property: string, value: string | number | null) => void;
  currentFilter: FilterState;
  sportName: string;
}

const PlayerNewsHeader: React.FC<PlayerNewsHeaderProps> = ({
  title,
  sportFilters,
  onFilterChange,
  currentFilter,
  sportName,
}) => {
  const { newsTypes, teams, positions } = sportFilters;

  const newsTypeOptions = (newsTypes ?? []).map((type) => ({
    label: type?.name ?? "",
  }));
  const teamOptions = (teams ?? []).map((team) => ({
    label: team?.name ?? "",
  }));
  const positionTypeOptions =
    (positions ?? []).map((position) => ({ label: position ?? "" })) || [];

  const selectedNewsTypeIndex = newsTypes?.findIndex(
    (newsType) =>
      newsType?.enum?.toLowerCase() ===
      currentFilter?.newsTypeFilter?.toLowerCase()
  );

  let selectedTeamIndex = 0;

  if (
    currentFilter?.teamFilter !== null &&
    currentFilter?.teamFilter !== undefined
  ) {
    const foundTeam = teams?.findIndex(
      (team) => team?.numberFireId === currentFilter.teamFilter
    );
    if (foundTeam >= 0) {
      selectedTeamIndex = foundTeam ?? 0;
    }
  }
  const { logAmplitudeEvent } = useAmplitude();
  const selectedPositionIndex = positions?.indexOf(
    currentFilter?.positionFilter
  );

  const transformPositionForApi = (value: number) =>
    (positions?.[value] as string) === "All" ? null : positions?.[value];

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{title}</h1>
      <PoweredByNumberFire />
      <div className={styles.filterContainer}>
        <Dropdown
          variant="standard"
          selected={
            selectedNewsTypeIndex === -1 || !selectedNewsTypeIndex
              ? 0
              : selectedNewsTypeIndex
          }
          setSelected={(value) => {
            const selectedNewsType = newsTypes?.[value as number];
            logAmplitudeEvent(PLAYER_NEWS_FILTER_APPLIED, {
              Module: "player_news_filters",
              "Filter Name": "news",
              "Filter Value": selectedNewsType?.name ?? "",
              Competition: sportName,
            });

            onFilterChange(
              "newsTypeFilter",
              selectedNewsType?.enum ? selectedNewsType.enum : null
            );
            const newUrlSegment = selectedNewsType?.enum
              ? String(findSlugByType(selectedNewsType.enum))
              : "";
            if (selectedNewsType) {
              replaceLastSegment(newUrlSegment, ["player-news"]);
            }
          }}
          title="News Type"
          options={newsTypeOptions}
        />

        <Dropdown
          variant="standard"
          selected={selectedTeamIndex === -1 ? 0 : selectedTeamIndex}
          setSelected={(value) => {
            onFilterChange(
              "teamFilter",
              teams?.[value as number]?.numberFireId ?? null
            );
            logAmplitudeEvent(PLAYER_NEWS_FILTER_APPLIED, {
              Module: "player_news_filters",
              "Filter Name": "team",
              "Filter Value": teams?.[value as number]?.name,
              Competition: sportName,
            });
          }}
          title="Team"
          options={teamOptions}
        />

        <Dropdown
          variant="standard"
          selected={
            selectedPositionIndex !== null &&
            selectedPositionIndex !== undefined &&
            selectedPositionIndex !== -1
              ? selectedPositionIndex
              : 0
          }
          setSelected={(value) => {
            logAmplitudeEvent(PLAYER_NEWS_FILTER_APPLIED, {
              Module: "player_news_filters",
              "Filter Name": "position",
              "Filter Value": positions?.[value as number] ?? "",
              Competition: sportName,
            });

            onFilterChange(
              "positionFilter",
              transformPositionForApi(value as number) ?? null
            );
          }}
          title="Position"
          options={positionTypeOptions}
        />
      </div>
    </div>
  );
};

export default PlayerNewsHeader;
